/**
 * The live preview's grid (CT-5199).
 *
 * The preview is a canvas the size of a dashboard row, not a box the size of
 * the column it happens to sit in — that is the whole reason it declares its
 * own column count instead of letting the grid derive one from ~530px of
 * available width.
 */

/** Dashboards are twelve columns wide, so the preview is too. */
export const PREVIEW_GRID_COLUMNS = 12;

/**
 * Every breakpoint gets the same twelve columns.
 *
 * The preview column does not get narrower on a small screen — the whole
 * playground stacks instead — and even if it did, a preview that silently
 * re-flowed to four columns would be showing a layout the author never chose.
 */
export const PREVIEW_GRID_COLS = Object.freeze({
  lg: PREVIEW_GRID_COLUMNS,
  md: PREVIEW_GRID_COLUMNS,
  sm: PREVIEW_GRID_COLUMNS,
  xs: PREVIEW_GRID_COLUMNS,
  xxs: PREVIEW_GRID_COLUMNS,
});

/** Tighter than a dashboard's 16px: the canvas is under half a page wide. */
export const PREVIEW_GRID_CONFIG = Object.freeze({
  rowHeight: 24,
  margin: [12, 12],
  cols: PREVIEW_GRID_COLS,
});

/** The one widget on the canvas. */
export const PREVIEW_WIDGET_ID = 'widget-builder-preview';

/**
 * `S` / `M` / `L` as column spans.
 *
 * These are the numbers the size captions have always claimed — "1/3 width",
 * "2/3 width", "Full width" — and until the preview sat on a real grid they
 * were an approximation drawn with `max-width`. Now they are the layout.
 */
export const COLUMN_SPAN_BY_SIZE = Object.freeze({
  small: 4,
  medium: 8,
  large: 12,
});

/** The three preview sizes, smallest first — the order the size rail shows. */
export const PREVIEW_SIZES = Object.freeze(['small', 'medium', 'large']);

/**
 * The size a column span reads as — the inverse of `COLUMN_SPAN_BY_SIZE`, for
 * spans that are not exactly 4, 8 or 12.
 *
 * Dragging the west or east handle lands the author on any span at all, and
 * the `S M L` rail has to keep telling the truth about what they are looking
 * at. Nearest wins; a tie goes to the smaller size, because a widget on the
 * boundary is the one the author has been shrinking.
 */
export const sizeForColumnSpan = (span) =>
  PREVIEW_SIZES.reduce((best, size) =>
    Math.abs(COLUMN_SPAN_BY_SIZE[size] - span) < Math.abs(COLUMN_SPAN_BY_SIZE[best] - span)
      ? size
      : best,
  );
